import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Package2, Mail, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LanguageSelector from '@/components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Unsubscribe() {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (token) {
      processUnsubscribe();
    }
  }, [token]);

  const processUnsubscribe = async () => {
    try {
      const response = await axios.get(`${API}/newsletter/unsubscribe/${token}`);
      setEmail(response.data.email || '');
      setStatus('success');
    } catch (error) {
      console.error('Unsubscribe failed:', error);
      setErrorMessage(error.response?.data?.detail || t('unsubscribe.errorMessage'));
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white flex flex-col">
      {/* Header */}
      <header className="py-6 px-4 border-b border-stone-100">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-stone-800 hover:text-primary transition-colors"
          >
            <Package2 className="h-8 w-8 text-primary" />
            <span className="text-2xl font-heading font-bold">Even Rounds</span>
          </button>
          <LanguageSelector variant="ghost" />
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="max-w-md w-full text-center">
          {status === 'loading' && (
            <div className="space-y-4">
              <Loader2 className="h-16 w-16 animate-spin text-primary mx-auto" />
              <h1 className="text-2xl font-heading font-bold">{t('unsubscribe.processing')}</h1>
              <p className="text-muted-foreground">{t('unsubscribe.pleaseWait')}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-6">
              <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-heading font-bold">{t('unsubscribe.success')}</h1>
              <p className="text-muted-foreground">
                {email ? (
                  <>
                    <strong>{email}</strong> {t('unsubscribe.successMessage')}
                  </>
                ) : (
                  t('unsubscribe.successGeneric')
                )}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('unsubscribe.sorryToSeeYouGo')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                <Button
                  onClick={() => navigate('/blog')}
                  className="rounded-full"
                >
                  {t('unsubscribe.visitBlog')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="rounded-full"
                >
                  {t('unsubscribe.goHome')}
                </Button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-6">
              <div className="h-20 w-20 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
              <h1 className="text-3xl font-heading font-bold">{t('unsubscribe.error')}</h1>
              <p className="text-muted-foreground">
                {errorMessage || t('unsubscribe.errorMessage')}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('unsubscribe.linkInvalid')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                <Button
                  onClick={() => navigate('/blog')}
                  className="rounded-full"
                >
                  {t('unsubscribe.visitBlog')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="rounded-full"
                >
                  {t('unsubscribe.goHome')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 px-4 border-t border-stone-100">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-stone-500">
            © 2026 Even Rounds. {t('common.madeWithLove')}
          </p>
        </div>
      </footer>
    </div>
  );
}
